import { getMessagingTicket } from "@/services/messagingApiService";
class MessagingSocketService {
    constructor() {
        Object.defineProperty(this, "socket", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "reconnectTimer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "heartbeatTimer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "reconnectAttempts", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "stopped", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: true
        });
        Object.defineProperty(this, "eventHandler", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "extraEventHandlers", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Set()
        });
        Object.defineProperty(this, "statusHandler", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "setHandlers", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (eventHandler, statusHandler) => {
                this.eventHandler = eventHandler;
                this.statusHandler = statusHandler;
            }
        });
        Object.defineProperty(this, "addEventListener", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (handler) => {
                this.extraEventHandlers.add(handler);
                return () => this.extraEventHandlers.delete(handler);
            }
        });
        Object.defineProperty(this, "connect", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async () => {
                if (!navigator.onLine || !localStorage.getItem("authToken"))
                    return;
                if (!this.stopped && this.socket?.readyState === WebSocket.OPEN)
                    return;
                if (!this.stopped && this.socket?.readyState === WebSocket.CONNECTING)
                    return;
                this.stopped = false;
                this.setStatus(this.reconnectAttempts ? "reconnecting" : "connecting");
                try {
                    const ticket = await getMessagingTicket();
                    if (this.stopped)
                        return;
                    const separator = ticket.websocketUrl.includes("?") ? "&" : "?";
                    const url = `${ticket.websocketUrl}${separator}ticket=${encodeURIComponent(ticket.ticket)}`;
                    this.socket = new WebSocket(url);
                    this.socket.onopen = () => {
                        this.reconnectAttempts = 0;
                        this.setStatus("connected");
                        this.startHeartbeat();
                    };
                    this.socket.onmessage = (message) => this.handleMessage(message);
                    this.socket.onerror = () => this.socket?.close();
                    this.socket.onclose = () => {
                        this.stopHeartbeat();
                        this.socket = null;
                        if (!this.stopped)
                            this.scheduleReconnect();
                        else
                            this.setStatus("disconnected");
                    };
                }
                catch {
                    if (!this.stopped)
                        this.scheduleReconnect();
                }
            }
        });
        Object.defineProperty(this, "disconnect", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.stopped = true;
                this.clearReconnect();
                this.stopHeartbeat();
                this.socket?.close();
                this.socket = null;
                this.setStatus("disconnected");
            }
        });
        Object.defineProperty(this, "send", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (action, payload = {}) => {
                if (this.socket?.readyState !== WebSocket.OPEN)
                    return false;
                const requestId = crypto.randomUUID();
                this.socket.send(JSON.stringify({ action, requestId, ...payload }));
                return true;
            }
        });
        Object.defineProperty(this, "reconnectIfNeeded", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                if (this.stopped)
                    return;
                if (this.socket?.readyState === WebSocket.OPEN)
                    return;
                void this.connect();
            }
        });
        Object.defineProperty(this, "handleMessage", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (message) => {
                try {
                    const event = JSON.parse(message.data);
                    this.eventHandler?.(event);
                    for (const handler of this.extraEventHandlers) {
                        handler(event);
                    }
                }
                catch {
                    // ignorar
                }
            }
        });
        Object.defineProperty(this, "scheduleReconnect", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.clearReconnect();
                this.reconnectAttempts += 1;
                const exponentialDelay = Math.min(30000, 1000 * 2 ** (this.reconnectAttempts - 1));
                const jitter = Math.round(Math.random() * 500);
                this.setStatus("reconnecting");
                this.reconnectTimer = setTimeout(() => void this.connect(), exponentialDelay + jitter);
            }
        });
        Object.defineProperty(this, "startHeartbeat", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.stopHeartbeat();
                this.heartbeatTimer = setInterval(() => {
                    this.send("ping", { timestamp: new Date().toISOString() });
                }, 45000);
            }
        });
        Object.defineProperty(this, "stopHeartbeat", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                if (this.heartbeatTimer)
                    clearInterval(this.heartbeatTimer);
                this.heartbeatTimer = null;
            }
        });
        Object.defineProperty(this, "clearReconnect", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                if (this.reconnectTimer)
                    clearTimeout(this.reconnectTimer);
                this.reconnectTimer = null;
            }
        });
        Object.defineProperty(this, "setStatus", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (status) => this.statusHandler?.(status)
        });
    }
}
export const messagingSocketService = new MessagingSocketService();
